import type { IOrderDraft, Order } from '@/types/types';
import { parseISO } from 'date-fns';
import type { RouteLocationRaw } from 'vue-router';
import { availableStepKeys } from '@/stores/orderCreation';

/**
 * Calculate the number of days between start and end date of an order
 */
export function calculateOrderNumberOfDays(order: Order | IOrderDraft): number {
  if (!order.startdate || !order.enddate) {
    return 0;
  }
  return Math.round((parseISO(order.enddate).getTime() - parseISO(order.startdate).getTime()) / (1000 * 60 * 60 * 24));
}

/**
 * Resolves the route for an order: drafts go back into the creation wizard at their
 * last known step, everything else goes to the order detail page. Every call site that
 * links/navigates to an order must go through this so drafts never land on the detail page.
 */
export function getOrderRoute(order: Order, selfServiceEnabled: boolean): RouteLocationRaw {
  if (!order.is_draft) {
    return { name: 'order.detail.info', params: { id: order.id } };
  }

  const query: Record<string, string> = { orderId: `${order.id}` };
  if (order.step) {
    const visibleSteps = availableStepKeys.filter((stepKey) => {
      // For direct URL orders, hide publication steps as they are not relevant
      if (order.creation_source === 'direct_url' && stepKey.includes('publication_')) return false;
      // If self-service is not enabled, go to the final step
      if (stepKey === 'self_service' && !selfServiceEnabled) return false;
      return true;
    });
    const foundStepIndex = visibleSteps.findIndex((stepKey) => stepKey === order.step);
    if (foundStepIndex !== -1) {
      // If the current step is the last one, keep it, otherwise go to the current/next step
      if (foundStepIndex === visibleSteps.length - 1) query['step'] = visibleSteps[visibleSteps.length - 1];
      else if (order.step.includes(':')) query['step'] = order.step;
      else query['step'] = visibleSteps[foundStepIndex + 1];
    }
  } else if (order.title || order.directlink || order.apply_url) {
    // The draft has something saved but the backend didn't persist a step
    // (CREATE_DRAFT drops the `step` field) — skip the stepless landing and
    // send the user straight into details:basic, the first actionable card.
    query['step'] = 'details:basic';
  }
  const pubsLength = order.publications?.data?.length || 0;
  if (pubsLength) {
    query['pubkey'] = order.publications!.data[pubsLength - 1].pubkey;
  }

  return { name: 'order.create', query };
}
